import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { unwrapResult } from '@reduxjs/toolkit';
import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, StripeCardNumberElementChangeEvent, StripeElementChangeEvent } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { LoadingProgress } from '../../layout';
import { PaymentMethodType } from '../../models';
import { useReadOnlyMode } from '../../routes';
import { fromAccounts, fromAccountUsers, fromAuth, fromPaymentMethod, useAppDispatch } from '../../store';
import { useLaunchDarkly } from '../../launchdarkly';
import { sendTrackingData } from '../../utils/utils';
import { track } from '../../analytics';
import { CARD_BRAND_LIST } from '../../components/payment-information-section/payment-method/Constants';
import { DeletePaymentMethodDialog } from './DeletePaymentMethodDialog';
import { SetDefaultPaymentMethodDialog } from './SetDefaultPaymentMethodDialog';

const useStyles = makeStyles(theme => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    margin: '30px 0 12px',
  },
  sectionTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15,
    fontWeight: 700,
    color: '#16141d',
  },
  linkButton: {
    border: 'none',
    background: 'none',
    color: '#0f8461',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 4,
    fontFamily: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: '#9aa5a0',
      cursor: 'not-allowed',
      textDecoration: 'none',
    },
  },
  cancelLink: {
    color: '#5b6661',
  },
  savedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 18px',
    border: '1px solid #e1e7e4',
    borderRadius: 14,
    backgroundColor: '#fff',
  },
  brandChip: {
    width: 48,
    height: 32,
    border: '1px solid #eceaf3',
    borderRadius: 7,
    backgroundColor: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    color: '#16141d',
  },
  brandChipLogo: {
    height: 15,
    width: 'auto',
    display: 'block',
  },
  savedName: {
    fontSize: 14.5,
    fontWeight: 700,
    color: '#16141d',
    textTransform: 'capitalize',
  },
  savedSub: {
    fontSize: 12.5,
    color: '#5b6661',
    marginTop: 2,
  },
  defaultPill: {
    flex: 'none',
    fontSize: 11,
    fontWeight: 700,
    color: '#0f8461',
    backgroundColor: '#e7f8f1',
    border: '1px solid #b6ebd6',
    padding: '3px 10px',
    borderRadius: 999,
  },
  methodTileRow: {
    display: 'flex',
    gap: 14,
  },
  methodTile: {
    flex: 1,
    textAlign: 'left',
    padding: '15px 17px',
    border: '1.5px solid #e1e7e4',
    borderRadius: 14,
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'border-color .15s ease, background .15s ease',
    '&:hover': {
      borderColor: '#bfe8d6',
    },
  },
  methodTileSelected: {
    borderColor: '#0f8461',
    backgroundColor: '#f2fbf7',
    '&:hover': {
      borderColor: '#0f8461',
    },
  },
  tileTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    color: '#16141d',
  },
  tileRadio: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #cdd6d2',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    transition: 'all .15s ease',
  },
  tileRadioSelected: {
    borderColor: '#0f8461',
    backgroundColor: '#0f8461',
  },
  tileRadioDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: 'transparent',
  },
  tileRadioDotSelected: {
    backgroundColor: '#fff',
  },
  tileName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#16141d',
    marginTop: 11,
  },
  tileHint: {
    fontSize: 12.5,
    color: '#5b6661',
    marginTop: 2,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12.5,
    fontWeight: 600,
    color: '#3c3a50',
    marginBottom: 6,
  },
  required: {
    color: '#0c6a4e',
  },
  cardBox: {
    border: '1px solid #e1e7e4',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  cardNumberRow: {
    position: 'relative',
    borderBottom: '1px solid #e1e7e4',
    '& .StripeElement': {
      padding: '15px 150px 0 14px',
      height: 46,
      boxSizing: 'border-box',
    },
  },
  cardNumberLogos: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    pointerEvents: 'none',
  },
  cardNumberLogo: {
    height: 14,
    width: 'auto',
    display: 'block',
  },
  cardExpCvcRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    '& .StripeElement': {
      padding: '15px 14px 0',
      height: 46,
      boxSizing: 'border-box',
    },
  },
  cardExpCell: {
    borderRight: '1px solid #e1e7e4',
  },
  plainExpiryInput: {
    width: '100%',
    height: 46,
    border: 'none',
    outline: 'none',
    // A native input vertically centers its text, unlike the Stripe elements whose
    // .StripeElement wrapper needs the 15px top padding — so no top padding here.
    padding: '0 14px',
    boxSizing: 'border-box',
    fontSize: 14,
    color: '#16141d',
    backgroundColor: 'transparent',
    fontFamily: 'Inter, system-ui, sans-serif',
    '&::placeholder': {
      color: '#5b6661',
    },
  },
  nameInput: {
    width: '100%',
    height: 44,
    border: '1px solid #e1e7e4',
    borderRadius: 8,
    padding: '0 13px',
    fontSize: 14,
    color: '#16141d',
    backgroundColor: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&:focus': {
      borderColor: '#0f8461',
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '&::placeholder': {
      color: '#5b6661',
    },
  },
  fieldError: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 7,
    color: '#c0392b',
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1.45,
    '& svg': {
      flex: 'none',
      marginTop: 1,
    },
  },
  secureNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    marginTop: 12,
    fontSize: 12.5,
    color: '#5b6661',
    lineHeight: 1.5,
  },
  secureNoteIcon: {
    display: 'inline-flex',
    flex: 'none',
    color: '#0f8461',
    marginTop: 1,
  },
  bankNote: {
    marginTop: 16,
    padding: '14px 16px',
    border: '1px solid #e1e7e4',
    borderRadius: 12,
    backgroundColor: '#faf9ff',
    fontSize: 13,
    color: '#5f5d75',
    lineHeight: 1.55,
  },
  actionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 18,
  },
  updateButton: {
    flex: 1,
    height: 46,
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#0f8461',
    color: '#fff',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#e7ebe9',
      color: '#9aa5a0',
      cursor: 'not-allowed',
    },
  },
  deleteButton: {
    flex: 'none',
    height: 46,
    padding: '0 20px',
    border: '1px solid #f1c4c4',
    borderRadius: 10,
    backgroundColor: '#fff',
    color: '#c0392b',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#fdecec',
      borderColor: '#e8a9a9',
    },
    '&:disabled': {
      color: '#9aa5a0',
      borderColor: '#e1e7e4',
      cursor: 'not-allowed',
    },
  },
  noMethodBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 18,
    border: '1px dashed #d7d5e6',
    borderRadius: 14,
    backgroundColor: '#f7f9f8',
  },
  noMethodTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#16141d',
  },
  noMethodSub: {
    fontSize: 12.5,
    color: '#5b6661',
    marginTop: 3,
  },
  addMethodButton: {
    flex: 'none',
    height: 40,
    padding: '0 18px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#0f8461',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#e7ebe9',
      color: '#9aa5a0',
      cursor: 'not-allowed',
    },
  },
}));

const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: '14px',
    color: '#16141d',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSmoothing: 'antialiased',
    '::placeholder': {
      color: '#5b6661',
    },
  },
  invalid: {
    color: '#e5484d',
  },
};

const CardIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
  </svg>
);

const BankIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10h16M5 10l7-4.5 7 4.5M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16" />
  </svg>
);

const CartIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const LockIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10" width="16" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const ErrorIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

const CARD_NUMBER_LOGO_BRANDS = ['visa', 'mastercard', 'americanexpress', 'discover'];

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

const PaymentMethodSectionInner = ({ accountId }: { accountId: number }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const elements = useElements();
  const stripe = useStripe();
  const { enqueueSnackbar } = useSnackbar();
  const { flags } = useLaunchDarkly();
  const isReadOnly = useReadOnlyMode();

  const user = useSelector(fromAuth.selectUser);
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const currentAccountUser = useSelector(fromAccountUsers.selectOneByUserIdAndAccountId(
    Number(user?.id),
    Number(accountId),
  ));
  const isPublicDomain = useSelector(fromAccounts.selectIsPublicDomain);
  const defaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const currentCard = useSelector(fromPaymentMethod.selectOneByAccountId(Number(accountId)));

  const [editing, setEditing] = useState(false);
  const [selectedType, setSelectedType] = useState<PaymentMethodType>(
    PaymentMethodType.CREDIT_CARD,
  );
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);

  const [cardName, setCardName] = useState('');
  const [plainExpiry, setPlainExpiry] = useState('');
  const [cardNumberState, setCardNumberState] = useState({ complete: false, empty: true });
  const [cardExpiryState, setCardExpiryState] = useState({ complete: false, empty: true });
  const [cardCvcState, setCardCvcState] = useState({ complete: false, empty: true });
  const [cardErrorMessage, setCardErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    const getPaymentMethod = async () => {
      if (accountId) {
        await dispatch(fromPaymentMethod.doGetPaymentMethod(accountId));
      }
    };
    getPaymentMethod().catch(() => {
    });
  }, [accountId, dispatch]);

  const currentDefaultType = defaultPaymentMethod ?? PaymentMethodType.CREDIT_CARD;

  // Stripe Elements cannot be prefilled (PCI) — the saved card can only be
  // hinted via placeholders. The cardholder name is a plain input, so it is
  // prefilled from the saved card and treated as untouched until changed.
  const prefilledCardName = currentCard?.name ?? '';

  // "12/2034" → "12 / 34", matching Stripe's default "MM / YY" placeholder shape.
  const savedExpiryDisplay = (() => {
    const [month, year] = (currentCard?.expiration ?? '').split('/');
    return month && year ? `${month.padStart(2, '0')} / ${year.slice(-2)}` : '';
  })();

  // Mirrors the legacy standalone-page gate: this page never carries monthly
  // items or an in-flight cart, so bank transfer availability only depends on
  // the flag, the public-domain check and the AWS special case.
  const showBankTile = currentDefaultType === PaymentMethodType.AWS_MARKETPLACE
    || (!!flags?.bankTransferEnabled && !isPublicDomain);

  const cardComplete = cardNumberState.complete && cardExpiryState.complete
    && cardCvcState.complete && cardName.trim() !== '';
  const cardTouched = !cardNumberState.empty || !cardExpiryState.empty
    || !cardCvcState.empty || cardName.trim() !== prefilledCardName.trim();
  const methodChanged = selectedType !== currentDefaultType;

  // Editing the saved card without typing a new number = in-place mode: only the
  // cardholder name and expiry are editable, and they are updated on the saved card
  // without re-tokenizing (no card number/CVC required). Typing a new number switches
  // back to the full replace-card form.
  const inPlaceMode = !!currentCard && cardNumberState.empty;
  const expiryDigits = (value: string) => value.replace(/\D/g, '');
  const expiryChanged = expiryDigits(plainExpiry) !== expiryDigits(savedExpiryDisplay);
  const plainExpiryValid = (() => {
    if (!/^(0[1-9]|1[0-2]) \/ \d{2}$/.test(plainExpiry)) return false;
    const month = Number(plainExpiry.slice(0, 2));
    const year = 2000 + Number(plainExpiry.slice(-2));
    const now = new Date();
    return year > now.getFullYear()
      || (year === now.getFullYear() && month >= now.getMonth() + 1);
  })();
  const detailsChanged = inPlaceMode
    && (cardName.trim() !== prefilledCardName.trim() || expiryChanged);
  // The expiry is only validated (and sent) when actually changed, so a saved card
  // whose stored expiry is missing or already past never blocks a name-only update.
  const detailsComplete = detailsChanged
    && (!expiryChanged || plainExpiryValid) && cardName.trim() !== '';

  const canUpdate = methodChanged
    ? (selectedType !== PaymentMethodType.CREDIT_CARD || !!currentCard || cardComplete)
    : (selectedType === PaymentMethodType.CREDIT_CARD
      && (inPlaceMode ? detailsComplete : cardComplete));

  const resetCardForm = () => {
    setCardName('');
    setPlainExpiry('');
    setCardErrorMessage(undefined);
    setCardNumberState({ complete: false, empty: true });
    setCardExpiryState({ complete: false, empty: true });
    setCardCvcState({ complete: false, empty: true });
    elements?.getElement(CardNumberElement)?.clear();
    elements?.getElement(CardExpiryElement)?.clear();
    elements?.getElement(CardCvcElement)?.clear();
  };

  const openEdit = () => {
    setSelectedType(currentDefaultType);
    setCardName(prefilledCardName);
    setPlainExpiry(savedExpiryDisplay);
    setEditing(true);
  };

  const closeEdit = () => {
    resetCardForm();
    setEditing(false);
  };

  const trackCardSaved = (eventName: string, gtmEvent: string) => {
    track(eventName, {
      user_id: user?.id,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      system_role: user?.roles,
      org_id: account?.id,
      org_name: account?.name,
      org_role: currentAccountUser?.role,
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      user_id: user?.id,
      org_role: currentAccountUser?.role,
      system_role: user?.roles,
      org_id: account?.id,
      org_name: account?.name,
      event: gtmEvent,
    });
    sendTrackingData('checkout_page_information_saved', accountId, undefined, {
      field: 'payment',
      object: 'button',
      action: 'save',
      is_required: true,
      is_valid: true,
    });
  };

  /** Tokenizes the entered card and creates/updates the saved payment method. */
  const saveCard = async (): Promise<boolean> => {
    if (!stripe || !elements) return false;
    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) return false;
    const token = await stripe.createToken(cardNumberElement, { name: cardName });
    if (token.error || !token.token) {
      setCardErrorMessage(token.error?.message
        ?? intl.formatMessage({ id: 'page.payment.method.card.invalid' }));
      return false;
    }
    if (currentCard) {
      await dispatch(fromPaymentMethod.doUpdatePaymentMethod({
        id: currentCard.id,
        stripeToken: token.token.id,
      })).then(unwrapResult);
      trackCardSaved('Payment Information Updated', 'gtm_payment_information_updated');
    } else {
      await dispatch(fromPaymentMethod.doCreatePaymentMethod({
        accountId: +accountId,
        stripeToken: token.token.id,
      })).then(unwrapResult);
      trackCardSaved('Payment Information Saved', 'gtm_payment_information_saved');
    }
    return true;
  };

  /** Updates the saved card's cardholder name/expiry in place — no re-tokenization. */
  const saveCardDetails = async (): Promise<boolean> => {
    if (!currentCard) return false;
    const month = Number(plainExpiry.slice(0, 2));
    const year = 2000 + Number(plainExpiry.slice(-2));
    await dispatch(fromPaymentMethod.doUpdatePaymentMethod({
      id: currentCard.id,
      name: cardName.trim(),
      expiration: expiryChanged ? `${month}/${year}` : undefined,
    })).then(unwrapResult);
    trackCardSaved('Payment Information Updated', 'gtm_payment_information_updated');
    return true;
  };

  const applyUpdate = async () => {
    setLoading(true);
    try {
      if (selectedType === PaymentMethodType.CREDIT_CARD
        && (inPlaceMode ? detailsChanged : cardTouched)) {
        if (inPlaceMode ? !detailsComplete : !cardComplete) {
          setCardErrorMessage(intl.formatMessage({ id: 'page.payment.method.card.invalid' }));
          return;
        }
        const saved = inPlaceMode ? await saveCardDetails() : await saveCard();
        if (!saved) return;
        if (!methodChanged) {
          enqueueSnackbar(<FormattedMessage
            id={currentCard ? 'billinginfo.cardinfo.update.success' : 'billinginfo.cardinfo.save.success'}
          />, { variant: 'success' });
        }
      }
      if (methodChanged) {
        await dispatch(fromPaymentMethod.doUpdateDefaultPaymentMethod({
          defaultPaymentMethod: selectedType,
        })).then(unwrapResult);
        dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(selectedType));
        enqueueSnackbar(<FormattedMessage
          id="billinginfo.default_payment_method.change.success"
        />, { variant: 'success' });
      }
      closeEdit();
    } catch (ex: any) {
      enqueueSnackbar(<span>{ex?.message}</span>, { variant: 'error' });
    } finally {
      setLoading(false);
      setDefaultDialogOpen(false);
    }
  };

  const handleUpdateClick = () => {
    if (selectedType === PaymentMethodType.CREDIT_CARD
      && (inPlaceMode ? (detailsChanged && !detailsComplete) : (cardTouched && !cardComplete))) {
      setCardErrorMessage(intl.formatMessage({ id: 'page.payment.method.card.invalid' }));
      return;
    }
    if (methodChanged) {
      setDefaultDialogOpen(true);
      return;
    }
    applyUpdate().catch(() => {
    });
  };

  const handleDeleteCard = async () => {
    if (!currentCard) return;
    setLoading(true);
    await dispatch(fromPaymentMethod.doDeletePaymentMethod({ id: currentCard.id }))
      .then(unwrapResult)
      .then(() => {
        setDeleteDialogOpen(false);
        closeEdit();
        enqueueSnackbar(<FormattedMessage id="billinginfo.cardinfo.delete.success" />, { variant: 'success' });
      })
      .catch(() => {
        // do nothing
      })
      .finally(() => setLoading(false));
  };

  const handleCardNumberChange = (e: StripeCardNumberElementChangeEvent) => {
    setCardNumberState({ complete: e.complete, empty: e.empty });
    if (currentCard && e.empty) {
      // Back to in-place mode — the Stripe expiry/CVC elements unmount, so their
      // change events will never fire again; drop their stale state here.
      setCardExpiryState({ complete: false, empty: true });
      setCardCvcState({ complete: false, empty: true });
    }
    setCardErrorMessage(e.error?.message);
  };

  const handlePlainExpiryChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    setPlainExpiry(digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits);
  };
  const handleExpiryChange = (e: StripeElementChangeEvent) => {
    setCardExpiryState({ complete: e.complete, empty: e.empty });
    setCardErrorMessage(e.error?.message);
  };
  const handleCvcChange = (e: StripeElementChangeEvent) => {
    setCardCvcState({ complete: e.complete, empty: e.empty });
    setCardErrorMessage(e.error?.message);
  };

  const methodLabel = (type: PaymentMethodType, withCardDetails: boolean) => {
    if (type === PaymentMethodType.CREDIT_CARD) {
      return withCardDetails && currentCard
        ? intl.formatMessage(
          { id: 'page.payment.method.card.ending' },
          { brand: currentCard.brand, last4: currentCard.last4 },
        )
        : intl.formatMessage({ id: 'page.order.confirmation.method.card' });
    }
    if (type === PaymentMethodType.BANK_TRANSFER) {
      return intl.formatMessage({ id: 'page.order.confirmation.method.bank' });
    }
    return intl.formatMessage({ id: 'billinginfo.payment_method.AWS_MARKETPLACE' });
  };

  const renderBrandChip = () => {
    const cardBrand = CARD_BRAND_LIST.find(it => it.brand === currentCard?.brand?.toLowerCase());
    return (
      <Box component="span" className={classes.brandChip}>
        {cardBrand
          ? <img src={cardBrand.src} alt={currentCard?.brand} className={classes.brandChipLogo} />
          : <CardIcon />}
      </Box>
    );
  };

  const renderSavedRow = () => {
    if (currentDefaultType === PaymentMethodType.BANK_TRANSFER) {
      return (
        <Box className={classes.savedRow} id="payment-method-saved-bank">
          <Box component="span" className={classes.brandChip}><BankIcon size={22} /></Box>
          <Box flex={1} minWidth={0}>
            <Typography className={classes.savedName}>
              <FormattedMessage id="page.order.confirmation.method.bank" />
            </Typography>
          </Box>
          <Box component="span" className={classes.defaultPill}>
            <FormattedMessage id="billinginfo.payment_method.default" />
          </Box>
        </Box>
      );
    }
    if (currentDefaultType === PaymentMethodType.AWS_MARKETPLACE) {
      return (
        <Box className={classes.savedRow} id="payment-method-saved-aws">
          <Box component="span" className={classes.brandChip}><CartIcon /></Box>
          <Box flex={1} minWidth={0}>
            <Typography className={classes.savedName}>
              <FormattedMessage id="billinginfo.payment_method.AWS_MARKETPLACE" />
            </Typography>
          </Box>
          <Box component="span" className={classes.defaultPill}>
            <FormattedMessage id="billinginfo.payment_method.default" />
          </Box>
        </Box>
      );
    }
    if (!currentCard) {
      return (
        <Box className={classes.noMethodBox} id="payment-method-none">
          <Box flex={1} minWidth={0}>
            <Typography className={classes.noMethodTitle}>
              <FormattedMessage id="page.payment.method.none.title" />
            </Typography>
            <Typography className={classes.noMethodSub}>
              <FormattedMessage id="page.payment.method.none.subtitle" />
            </Typography>
          </Box>
          <button
            type="button"
            id="payment-method-add-btn"
            className={classes.addMethodButton}
            onClick={openEdit}
            disabled={isReadOnly}
          >
            <FormattedMessage id="page.payment.method.none.add" />
          </button>
        </Box>
      );
    }
    return (
      <Box className={classes.savedRow} id="payment-method-saved-card">
        {renderBrandChip()}
        <Box flex={1} minWidth={0}>
          <Typography className={classes.savedName}>
            <FormattedMessage
              id="page.payment.method.card.ending"
              values={{ brand: currentCard.brand, last4: currentCard.last4 }}
            />
          </Typography>
          <Typography className={classes.savedSub}>
            <FormattedMessage
              id="page.payment.method.card.expires"
              values={{ expiration: currentCard.expiration }}
            />
          </Typography>
        </Box>
        <Box component="span" className={classes.defaultPill}>
          <FormattedMessage id="billinginfo.payment_method.default" />
        </Box>
      </Box>
    );
  };

  const renderMethodTile = (
    type: PaymentMethodType,
    icon: JSX.Element,
    nameId: string,
    hintId: string,
  ) => {
    const selected = selectedType === type;
    return (
      <button
        key={type}
        type="button"
        className={`${classes.methodTile} ${selected ? classes.methodTileSelected : ''}`}
        onClick={() => setSelectedType(type)}
      >
        <Box className={classes.tileTop}>
          <Box component="span" sx={{ display: 'inline-flex' }}>{icon}</Box>
          <Box component="span" className={`${classes.tileRadio} ${selected ? classes.tileRadioSelected : ''}`}>
            <Box component="span" className={`${classes.tileRadioDot} ${selected ? classes.tileRadioDotSelected : ''}`} />
          </Box>
        </Box>
        <Typography className={classes.tileName}>
          <FormattedMessage id={nameId} />
        </Typography>
        <Typography className={classes.tileHint}>
          <FormattedMessage id={hintId} />
        </Typography>
      </button>
    );
  };

  const renderCardForm = () => (
    <Box mt={2.25}>
      <Box component="span" className={classes.fieldLabel}>
        <FormattedMessage id="page.payment.method.card.info" />
        {' '}
        <span className={classes.required}>*</span>
      </Box>
      <Box className={classes.cardBox}>
        <Box className={classes.cardNumberRow}>
          <CardNumberElement
            options={{
              style: STRIPE_ELEMENT_STYLE,
              placeholder: currentCard
                ? `•••• •••• •••• ${currentCard.last4}`
                : '1234 1234 1234 1234',
            }}
            onChange={handleCardNumberChange}
          />
          <Box component="span" className={classes.cardNumberLogos}>
            {CARD_BRAND_LIST
              .filter(it => CARD_NUMBER_LOGO_BRANDS.includes(it.brand))
              .map(it => (
                <img
                  key={it.brand}
                  src={it.src}
                  alt={it.brand}
                  className={classes.cardNumberLogo}
                />
              ))}
          </Box>
        </Box>
        <Box
          className={classes.cardExpCvcRow}
          style={inPlaceMode ? { gridTemplateColumns: '1fr' } : undefined}
        >
          {inPlaceMode
            ? (
              // Keeping the saved card: expiry is a plain, editable input (updated on the
              // saved card without re-tokenizing) and the CVC is not needed.
              <Box>
                <input
                  id="payment-method-plain-expiry"
                  className={classes.plainExpiryInput}
                  value={plainExpiry}
                  placeholder="MM / YY"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => (
                    handlePlainExpiryChange(e.target.value)
                  )}
                />
              </Box>
            )
            : (
              <>
                <Box className={classes.cardExpCell}>
                  <CardExpiryElement
                    options={{ style: STRIPE_ELEMENT_STYLE }}
                    onChange={handleExpiryChange}
                  />
                </Box>
                <Box>
                  <CardCvcElement
                    options={{ style: STRIPE_ELEMENT_STYLE, placeholder: 'CVC' }}
                    onChange={handleCvcChange}
                  />
                </Box>
              </>
            )}
        </Box>
      </Box>
      {cardErrorMessage && (
        <Box className={classes.fieldError} role="alert">
          <ErrorIcon />
          {cardErrorMessage}
        </Box>
      )}
      <label className={classes.fieldLabel} style={{ marginTop: 14 }} htmlFor="payment-method-card-name">
        <FormattedMessage id="page.payment.method.card.holder" />
        {' '}
        <span className={classes.required}>*</span>
      </label>
      <input
        id="payment-method-card-name"
        className={classes.nameInput}
        value={cardName}
        placeholder={intl.formatMessage({ id: 'page.payment.method.card.holder.placeholder' })}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCardName(e.target.value)}
      />
      <Box className={classes.secureNote}>
        <Box component="span" className={classes.secureNoteIcon}><LockIcon /></Box>
        <span><FormattedMessage id="page.payment.method.card.note" /></span>
      </Box>
    </Box>
  );

  const renderEdit = () => (
    <>
      <Box className={classes.methodTileRow}>
        {renderMethodTile(
          PaymentMethodType.CREDIT_CARD,
          <CardIcon />,
          'page.order.confirmation.method.card',
          'page.order.confirmation.method.card.hint',
        )}
        {showBankTile && renderMethodTile(
          PaymentMethodType.BANK_TRANSFER,
          <BankIcon />,
          'page.order.confirmation.method.bank',
          'page.order.confirmation.method.bank.hint',
        )}
        {currentDefaultType === PaymentMethodType.AWS_MARKETPLACE && renderMethodTile(
          PaymentMethodType.AWS_MARKETPLACE,
          <CartIcon />,
          'page.order.confirmation.method.aws',
          'page.order.confirmation.method.aws.hint',
        )}
      </Box>
      {selectedType === PaymentMethodType.CREDIT_CARD && renderCardForm()}
      {selectedType === PaymentMethodType.BANK_TRANSFER && (
        <Box className={classes.bankNote}>
          <FormattedMessage id="page.payment.method.bank.note" />
        </Box>
      )}
      <Box className={classes.actionRow}>
        <button
          type="button"
          id="payment-method-update-btn"
          className={classes.updateButton}
          onClick={handleUpdateClick}
          disabled={!canUpdate || loading || isReadOnly}
        >
          <FormattedMessage id="page.payment.method.update" />
        </button>
        {!!currentCard && (
          <button
            type="button"
            id="payment-method-delete-btn"
            className={classes.deleteButton}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={loading || isReadOnly}
          >
            <FormattedMessage id="page.payment.method.delete_card" />
          </button>
        )}
      </Box>
    </>
  );

  const hasVisibleMethod = currentDefaultType !== PaymentMethodType.CREDIT_CARD || !!currentCard;

  return (
    <Box id="payment-method-section">
      <Box className={classes.headerRow}>
        <Typography className={classes.sectionTitle}>
          <FormattedMessage id="page.payment.method.title" />
        </Typography>
        {editing
          ? (
            <button type="button" className={`${classes.linkButton} ${classes.cancelLink}`} onClick={closeEdit}>
              <FormattedMessage id="page.payment.method.cancel" />
            </button>
          )
          : hasVisibleMethod && (
            <button
              type="button"
              id="payment-method-edit-btn"
              className={classes.linkButton}
              onClick={openEdit}
              disabled={isReadOnly}
            >
              <FormattedMessage id="page.payment.method.edit" />
            </button>
          )}
      </Box>
      {editing ? renderEdit() : renderSavedRow()}
      <DeletePaymentMethodDialog
        open={deleteDialogOpen}
        methodLabel={methodLabel(PaymentMethodType.CREDIT_CARD, true)}
        accountName={account?.name}
        loading={loading}
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          handleDeleteCard().catch(() => {
          });
        }}
      />
      <SetDefaultPaymentMethodDialog
        open={defaultDialogOpen}
        methodLabel={methodLabel(selectedType, false)}
        oldMethodLabel={methodLabel(currentDefaultType, true)}
        accountName={account?.name}
        loading={loading}
        onCancel={() => setDefaultDialogOpen(false)}
        onConfirm={() => {
          applyUpdate().catch(() => {
          });
        }}
      />
      {loading && <LoadingProgress fullscreen />}
    </Box>
  );
};

export const PaymentMethodSection = ({ accountId }: { accountId: number }) => (
  <Elements stripe={stripePromise}>
    <PaymentMethodSectionInner accountId={accountId} />
  </Elements>
);
